import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMemo } from 'react';
import { getGrade, getHospitalGrade } from '@/lib/grades';
import { loadStaticData } from '@/lib/offlineData';

export type BaselineType = 'mean' | 'median';

export interface StateChargemasterStats {
  state: string;
  hospitalCount: number;
  // Mean baseline (markup vs national mean prices)
  medianMarkup: number;
  avgMarkup: number;
  cptMedian: number | null;
  drgMedian: number | null;
  revMedian: number | null;
  hcpcsMedian: number | null;
  drugMedian: number | null;
  supplyMedian: number | null;
  gradeA: number;
  gradeB: number;
  gradeC: number;
  gradeD: number;
  gradeF: number;
  bestHospital: { name: string; markup: number } | null;
  worstHospital: { name: string; markup: number } | null;
  // Median baseline (markup vs national median prices)
  medianMarkupMB: number;
  avgMarkupMB: number;
  cptMedianMB: number | null;
  drgMedianMB: number | null;
  revMedianMB: number | null;
  hcpcsMedianMB: number | null;
  drugMedianMB: number | null;
  supplyMedianMB: number | null;
  gradeAMB: number;
  gradeBMB: number;
  gradeCMB: number;
  gradeDMB: number;
  gradeFMB: number;
  bestHospitalMB: { name: string; markup: number } | null;
  worstHospitalMB: { name: string; markup: number } | null;
}

function calcMedian(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// Return type: { source: 'static', stats } or { source: 'raw', rankings }
type QueryResult =
  | { source: 'static'; stats: StateChargemasterStats[] }
  | { source: 'raw'; rankings: any[] };

export function useStateChargemasterStats() {
  const { data: queryResult, isLoading, error } = useQuery<QueryResult>({
    queryKey: ['state-chargemaster-stats-v2'],
    queryFn: async (): Promise<QueryResult> => {
      // Try static data first (encoded .dat on Capacitor, .json on web)
      try {
        const staticData = await loadStaticData<{ generated_at: string; data: StateChargemasterStats[] }>('states.json');
        if (staticData && Array.isArray(staticData.data) && staticData.data.length > 0) {
          console.log(`[StateStats] Using static data (${staticData.data.length} states)`);
          return { source: 'static', stats: staticData.data };
        }
      } catch (e) {
        console.warn('[StateStats] Static data error, falling back to API:', e);
      }

      // Fallback: fetch raw ranking data from Supabase
      const { data: rankings, error: rankErr } = await supabase
        .from('hospital_ranking_summary')
        .select(`
          hospital_id,
          avg_gross_markup_percentage,
          cpt_avg_markup_percentage,
          drg_avg_markup_percentage,
          rev_avg_markup_percentage,
          hcpcs_avg_markup_percentage,
          drug_avg_markup_percentage,
          supply_avg_markup_percentage,
          median_gross_markup_percentage,
          cpt_median_markup_percentage,
          drg_median_markup_percentage,
          rev_median_markup_percentage,
          hcpcs_median_markup_percentage,
          drug_median_markup_percentage,
          supply_median_markup_percentage,
          hospital_name,
          state
        `)
        .not('avg_gross_markup_percentage', 'is', null);

      if (rankErr) throw rankErr;
      return { source: 'raw', rankings: rankings || [] };
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const stats = useMemo(() => {
    if (!queryResult) return [];

    // If we got pre-aggregated stats from static cache, use them directly
    if (queryResult.source === 'static') {
      return queryResult.stats;
    }

    // Otherwise, aggregate raw ranking data client-side
    const rawData = queryResult.rankings;
    if (!rawData || rawData.length === 0) return [];

    const US_STATES = new Set([
      'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
      'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
      'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
      'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
      'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
    ]);

    // Group by state
    const byState: Record<string, typeof rawData> = {};
    for (const row of rawData) {
      const st = row.state;
      if (!st || !US_STATES.has(st)) continue;
      if (!byState[st]) byState[st] = [];
      byState[st].push(row);
    }

    const results: StateChargemasterStats[] = [];
    const nonZero = (v: number | null): v is number => v != null && Number(v) !== 0;

    for (const [state, hospitals] of Object.entries(byState)) {
      // Mean-baseline markups
      const markups = hospitals.map(h => Number(h.avg_gross_markup_percentage));

      // Median-baseline markups (fallback to mean if median not yet populated)
      const markupsMB = hospitals.map(h => {
        const mb = h.median_gross_markup_percentage;
        return mb != null ? Number(mb) : Number(h.avg_gross_markup_percentage);
      });

      // Grade distribution — mean baseline
      let gradeA = 0, gradeB = 0, gradeC = 0, gradeD = 0, gradeF = 0;
      for (const m of markups) {
        const g = getGrade(m);
        if (g === 'A') gradeA++;
        else if (g === 'B') gradeB++;
        else if (g === 'C') gradeC++;
        else if (g === 'D') gradeD++;
        else gradeF++;
      }

      // Grade distribution — median baseline (hospital-level scale: the
      // thresholds are quantile-matched for median-baseline values)
      let gradeAMB = 0, gradeBMB = 0, gradeCMB = 0, gradeDMB = 0, gradeFMB = 0;
      for (const m of markupsMB) {
        const g = getHospitalGrade(m);
        if (g === 'A') gradeAMB++;
        else if (g === 'B') gradeBMB++;
        else if (g === 'C') gradeCMB++;
        else if (g === 'D') gradeDMB++;
        else gradeFMB++;
      }

      // Per-code-type medians — mean baseline
      const cptVals = hospitals.map(h => h.cpt_avg_markup_percentage).filter(nonZero).map(Number);
      const drgVals = hospitals.map(h => h.drg_avg_markup_percentage).filter(nonZero).map(Number);
      const revVals = hospitals.map(h => h.rev_avg_markup_percentage).filter(nonZero).map(Number);
      const hcpcsVals = hospitals.map(h => h.hcpcs_avg_markup_percentage).filter(nonZero).map(Number);
      const drugVals = hospitals.map(h => h.drug_avg_markup_percentage).filter(nonZero).map(Number);
      const supplyVals = hospitals.map(h => h.supply_avg_markup_percentage).filter(nonZero).map(Number);

      // Per-code-type medians — median baseline
      const cptValsMB = hospitals.map(h => h.cpt_median_markup_percentage).filter(nonZero).map(Number);
      const drgValsMB = hospitals.map(h => h.drg_median_markup_percentage).filter(nonZero).map(Number);
      const revValsMB = hospitals.map(h => h.rev_median_markup_percentage).filter(nonZero).map(Number);
      const hcpcsValsMB = hospitals.map(h => h.hcpcs_median_markup_percentage).filter(nonZero).map(Number);
      const drugValsMB = hospitals.map(h => h.drug_median_markup_percentage).filter(nonZero).map(Number);
      const supplyValsMB = hospitals.map(h => h.supply_median_markup_percentage).filter(nonZero).map(Number);

      // Best/worst hospital — mean baseline
      let best: { name: string; markup: number } | null = null;
      let worst: { name: string; markup: number } | null = null;
      for (const h of hospitals) {
        const m = Number(h.avg_gross_markup_percentage);
        const name = h.hospital_name || 'Unknown';
        if (!best || m < best.markup) best = { name, markup: m };
        if (!worst || m > worst.markup) worst = { name, markup: m };
      }

      // Best/worst hospital — median baseline
      let bestMB: { name: string; markup: number } | null = null;
      let worstMB: { name: string; markup: number } | null = null;
      for (const h of hospitals) {
        const mb = h.median_gross_markup_percentage;
        const m = mb != null ? Number(mb) : Number(h.avg_gross_markup_percentage);
        const name = h.hospital_name || 'Unknown';
        if (!bestMB || m < bestMB.markup) bestMB = { name, markup: m };
        if (!worstMB || m > worstMB.markup) worstMB = { name, markup: m };
      }

      results.push({
        state,
        hospitalCount: hospitals.length,
        // Mean baseline
        medianMarkup: calcMedian(markups),
        avgMarkup: markups.reduce((a, b) => a + b, 0) / markups.length,
        cptMedian: cptVals.length > 0 ? calcMedian(cptVals) : null,
        drgMedian: drgVals.length > 0 ? calcMedian(drgVals) : null,
        revMedian: revVals.length > 0 ? calcMedian(revVals) : null,
        hcpcsMedian: hcpcsVals.length > 0 ? calcMedian(hcpcsVals) : null,
        drugMedian: drugVals.length > 0 ? calcMedian(drugVals) : null,
        supplyMedian: supplyVals.length > 0 ? calcMedian(supplyVals) : null,
        gradeA, gradeB, gradeC, gradeD, gradeF,
        bestHospital: best,
        worstHospital: worst,
        // Median baseline
        medianMarkupMB: calcMedian(markupsMB),
        avgMarkupMB: markupsMB.reduce((a, b) => a + b, 0) / markupsMB.length,
        cptMedianMB: cptValsMB.length > 0 ? calcMedian(cptValsMB) : null,
        drgMedianMB: drgValsMB.length > 0 ? calcMedian(drgValsMB) : null,
        revMedianMB: revValsMB.length > 0 ? calcMedian(revValsMB) : null,
        hcpcsMedianMB: hcpcsValsMB.length > 0 ? calcMedian(hcpcsValsMB) : null,
        drugMedianMB: drugValsMB.length > 0 ? calcMedian(drugValsMB) : null,
        supplyMedianMB: supplyValsMB.length > 0 ? calcMedian(supplyValsMB) : null,
        gradeAMB, gradeBMB, gradeCMB, gradeDMB, gradeFMB,
        bestHospitalMB: bestMB,
        worstHospitalMB: worstMB,
      });
    }

    return results.sort((a, b) => b.medianMarkup - a.medianMarkup);
  }, [queryResult]);

  // National stats (for both baselines)
  const nationalStats = useMemo(() => {
    if (!stats || stats.length === 0) return null;
    const allMarkups = stats.map(s => s.medianMarkup);
    const allMarkupsMB = stats.map(s => s.medianMarkupMB);
    return {
      medianMarkup: calcMedian(allMarkups),
      medianMarkupMB: calcMedian(allMarkupsMB),
      totalHospitals: stats.reduce((a, s) => a + s.hospitalCount, 0),
      mostExpensive: stats[0],
      cheapest: stats[stats.length - 1],
    };
  }, [stats]);

  return { stats, nationalStats, isLoading, error };
}
